import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { guestPortalApi } from './api';

const GuestAuthContext = createContext(null);

export function GuestAuthProvider({ children }) {
  const [guest, setGuest] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load stored auth on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('guest_token');
    const storedGuest = localStorage.getItem('guest_info');

    if (storedToken && storedGuest) {
      setToken(storedToken);
      try {
        setGuest(JSON.parse(storedGuest));
      } catch (e) {
        console.error('Failed to parse guest info:', e);
        localStorage.removeItem('guest_info');
      }
    }
    setIsLoading(false);
  }, []);

  // Verify token is still valid
  useEffect(() => {
    if (token) {
      guestPortalApi.getProfile(token)
        .then(response => {
          if (response.data) {
            setGuest(response.data);
            localStorage.setItem('guest_info', JSON.stringify(response.data));
          }
        })
        .catch(error => {
          console.error('Token validation failed:', error);
          if (error.response?.status === 401) {
            // Token is invalid, clear auth
            logout();
          }
        });
    }
  }, [token]);

  const login = useCallback((data) => {
    const { access_token, guest: guestData } = data;
    localStorage.setItem('guest_token', access_token);
    localStorage.setItem('guest_info', JSON.stringify(guestData));
    setToken(access_token);
    setGuest(guestData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('guest_token');
    localStorage.removeItem('guest_info');
    setToken(null);
    setGuest(null);
  }, []);

  const updateGuest = useCallback((updatedGuest) => {
    setGuest(updatedGuest);
    localStorage.setItem('guest_info', JSON.stringify(updatedGuest));
  }, []);

  return (
    <GuestAuthContext.Provider
      value={{
        guest,
        token,
        isLoading,
        isAuthenticated: !!token && !!guest,
        login,
        logout,
        updateGuest,
      }}
    >
      {children}
    </GuestAuthContext.Provider>
  );
}

export function useGuestAuth() {
  const context = useContext(GuestAuthContext);
  if (!context) {
    throw new Error('useGuestAuth must be used within a GuestAuthProvider');
  }
  return context;
}
